// ─────────────────────────────────────────────────────────────────────────────
// useLogoutSync.js  — drop this hook into any component that holds user state
// (Header, HomePage, AuthContext, etc.)
//
// It listens for the "user-logged-out" event that ProfileDashboard dispatches
// and wipes the component's in-memory user / auth state immediately, so the
// profile icon disappears without needing a page refresh.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect } from "react";

/**
 * @param {Function} onLogout  Called when the "user-logged-out" event fires.
 *                             Use it to reset whatever state your component holds
 *                             (setCurrentUser(null), setIsLoggedIn(false), etc.)
 */
export const useLogoutSync = (onLogout) => {
  useEffect(() => {
    const handler = () => onLogout();
    window.addEventListener("user-logged-out", handler);
    return () => window.removeEventListener("user-logged-out", handler);
  }, [onLogout]);
};

// ─────────────────────────────────────────────────────────────────────────────
// Usage example — paste the relevant lines into your Header / HomePage:
// ─────────────────────────────────────────────────────────────────────────────

/*
import { useState, useCallback } from "react";
import { useLogoutSync } from "./useLogoutSync";

const Header = () => {
  // Read initial user from localStorage (runs once on mount)
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const raw = localStorage.getItem("currentUser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // When logout fires from ProfileDashboard (or anywhere), clear state here too
  const handleLogout = useCallback(() => setCurrentUser(null), []);
  useLogoutSync(handleLogout);

  return (
    <header>
      {currentUser ? (
        // Profile icon — only shown when user is genuinely logged in
        <img
          src={currentUser.profile_image || fallbackAvatar}
          alt={currentUser.name}
          onClick={() => navigate("/profile")}
        />
      ) : (
        <button onClick={() => navigate("/login")}>Login</button>
      )}
    </header>
  );
};
*/

// ─────────────────────────────────────────────────────────────────────────────
// If you use an AuthContext / store, update it there instead:
// ─────────────────────────────────────────────────────────────────────────────

/*
// AuthContext.jsx
import { createContext, useContext, useState, useEffect, useCallback } from "react";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("currentUser");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // Keep context in sync when ProfileDashboard wipes localStorage and fires the event
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener("user-logged-out", handler);
    return () => window.removeEventListener("user-logged-out", handler);
  }, []);

  const login = useCallback((userData) => {
    localStorage.setItem("currentUser", JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    // ProfileDashboard already handles the full wipe + hard-navigate.
    // This is only needed if you ever call logout from outside ProfileDashboard.
    localStorage.removeItem("currentUser");
    setUser(null);
    window.location.replace("/");
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
*/